import { makeObservable, observable, action } from "mobx";
import SalesAnalyticsDashboardAPI from "../api/SalesAnalyticsDashboardAPI";
import { SalesAnalyticsSelectedFilters } from "../models/Dashboard/SalesDashboard/SalesAnalyticsFilters";
import { FilterValue } from "../models/Filters/FilterValue";
import {
  DailyIncentive,
  PipelineStage,
  pipelineStages,
  SalesAnalyticsData,
} from "../models/Dashboard/SalesDashboard/SalesAnalytics";
import { PipelineData } from "../models/Dashboard/SalesDashboard/SalesInterfaces";
import AdminTeamsAPI from "../api/AdminTeamsAPI";
import { AdminTeam } from "../models/AdminTeam/AdminTeam";

export class SalesAnalyticsDashboardStore {
  analyticsAPI: SalesAnalyticsDashboardAPI;
  adminTeamsAPI: AdminTeamsAPI;
  response: SalesAnalyticsData | null = null;
  owners: FilterValue[] = [];
  subTeams: AdminTeam[] = [];
  performance: { category: string; value: number; weightage: number }[] = [];
  overallPerformance: number = 0;
  incentivesData: DailyIncentive[] = [];
  leadsPipelines: { [key: number]: PipelineData } = {};
  dealsPipelines: { [key: number]: PipelineData } = {};
  pipelinePages: { [key: string]: number } = {
    leads_page_no_1: 1,
    leads_page_no_100: 1,
    leads_page_no_75: 1,
    leads_page_no_50: 1,
    leads_page_no_25: 1,
    leads_page_no_0: 1,
    deals_page_no_1: 1,
    deals_page_no_100: 1,
    deals_page_no_75: 1,
    deals_page_no_50: 1,
    deals_page_no_25: 1,
    deals_page_no_0: 1,
  };

  constructor() {
    this.analyticsAPI = new SalesAnalyticsDashboardAPI();
    this.adminTeamsAPI = new AdminTeamsAPI();
    this.performance = [];
    this.overallPerformance = 0;

    makeObservable(this, {
      response: observable,
      owners: observable,
      subTeams: observable,
      incentivesData: observable,
      leadsPipelines: observable,
      dealsPipelines: observable,
      pipelinePages: observable,
      getOwners: action,
      fetchIncentivesData: action,
      fetchLeadsPipelineData: action,
      fetchDealsPipelineData: action,
      updatePipelinePage: action,
      fetchAllPipelineData: action,
      setPipelinePages: action,
      replacePipelinePages: action,
      getSubTeams: action,
    });
  }

  setPipelinePages = (pages: { [key: string]: number }) => {
    this.pipelinePages = { ...this.pipelinePages, ...pages };
  };

  replacePipelinePages = (pages: { [key: string]: number }) => {
    this.pipelinePages = { ...pages };
  };

  getSalesAnalyticsData = async (filters: SalesAnalyticsSelectedFilters) => {
    try {
      const res = await this.analyticsAPI.getSalesAnalyticsData(filters);
      this.response = res;
      this.calculatePerformance();
      return res;
    } catch (error) {
      throw error;
    }
  };

  getOwners = async (searchText?: string) => {
    try {
      const response = await this.analyticsAPI.getOwners(searchText);
      this.owners = response.list;
    } catch (error) {
      throw error;
    }
  };

  getSubTeams = async (parent_team_id: number) => {
    try {
      const response = await this.adminTeamsAPI.getSubTeams(parent_team_id);
      this.subTeams = response.result || [];
    } catch (error) {
      throw error;
    }
  };

  calculatePerformance = () => {
    const achievements = this.response?.targets_and_achievements || [];

    this.performance = achievements.map((item) => {
      const achieved = Number(item.achieved_value);
      const target = Number(item.total_target);
      const weightage = Number(item.total_weightage) || 0;

      let value = 0;
      if (target > 0 && Number.isFinite(achieved)) {
        const percent = (achieved / target) * 100;
        value = Number.isFinite(percent) ? parseFloat(percent.toFixed(2)) : 0;
      }

      return {
        category: item.category_name || "Unknown",
        value,
        weightage,
      };
    });

    // Calculate weighted overall performance
    let weightedTotal = 0;
    let totalWeightage = 0;

    this.performance.forEach((metric) => {
      if (metric.weightage > 0 && Number.isFinite(metric.value)) {
        weightedTotal += metric.value * metric.weightage;
        totalWeightage += metric.weightage;
      }
    });

    this.overallPerformance = 0;
    if (totalWeightage > 0) {
      const raw = weightedTotal / totalWeightage;
      this.overallPerformance = Number.isFinite(raw)
        ? raw % 1 === 0
          ? raw
          : parseFloat(raw.toFixed(2))
        : 0;
    }
  };

  fetchClosedLeadsPipelineData = async (
    filters: SalesAnalyticsSelectedFilters,
  ) => {
    try {
      const stage = PipelineStage.CLOSED;
      const pageKey = `leads_page_no_${stage}`;
      const page = this.pipelinePages[pageKey] || 1;

      const res = await this.analyticsAPI.getLeadsPipeline(
        {
          ...filters,
          stage,
          stagePage: page,
        },
        stage,
      );

      this.leadsPipelines = {
        ...this.leadsPipelines,
        [stage]: {
          page_no: res.page_no || page,
          list: res.list || [],
          total_pages: res.total_pages,
          total_count: res.total_count,
          limit: res.limit,
          amount: res.amount || 0,
        },
      };
    } catch (error) {
      throw error;
    }
  };

  fetchLeadsPipelineData = async (
    filters: SalesAnalyticsSelectedFilters,
    stage: number,
  ) => {
    try {
      const pageKey = `leads_page_no_${stage}`;
      const page = this.pipelinePages[pageKey] || 1;

      const res = await this.analyticsAPI.getLeadsPipeline(
        {
          ...filters,
          stage,
          stagePage: page,
        },
        stage,
      );

      this.leadsPipelines = {
        ...this.leadsPipelines,
        [stage]: {
          page_no: res.page_no || page,
          list: res.list || [],
          total_pages: res.total_pages,
          total_count: res.total_count,
          limit: res.limit,
          amount: res.amount || 0,
        },
      };
    } catch (error) {
      throw error;
    }
  };

  fetchClosedDealsPipelineData = async (
    filters: SalesAnalyticsSelectedFilters,
  ) => {
    try {
      const stage = PipelineStage.CLOSED;
      const pageKey = `deals_page_no_${stage}`;
      const page = this.pipelinePages[pageKey] || 1;

      const res = await this.analyticsAPI.getDealsPipeline(
        {
          ...filters,
          stage,
          stagePage: page,
        },
        stage,
      );

      this.dealsPipelines = {
        ...this.dealsPipelines,
        [stage]: {
          page_no: res.page_no || page,
          list: res.list || [],
          total_pages: res.total_pages,
          total_count: res.total_count,
          limit: res.limit,
          amount: res.amount || 0,
        },
      };
    } catch (error) {
      throw error;
    }
  };

  fetchDealsPipelineData = async (
    filters: SalesAnalyticsSelectedFilters,
    stage: number,
  ) => {
    try {
      const pageKey = `deals_page_no_${stage}`;
      const page = this.pipelinePages[pageKey] || 1;

      const res = await this.analyticsAPI.getDealsPipeline(
        {
          ...filters,
          stage,
          stagePage: page,
        },
        stage,
      );

      this.dealsPipelines = {
        ...this.dealsPipelines,
        [stage]: {
          page_no: res.page_no || page,
          list: res.list || [],
          total_pages: res.total_pages,
          total_count: res.total_count,
          limit: res.limit,
          amount: res.amount || 0,
        },
      };
    } catch (error) {
      throw error;
    }
  };

  fetchAllPipelineData = async (filters: SalesAnalyticsSelectedFilters) => {
    try {
      const stages = pipelineStages;
      const leadsPromises = stages
        .filter((stage) => stage !== PipelineStage.CLOSED)
        .map((stage) => this.fetchLeadsPipelineData(filters, stage));
      const closedLeadsPromises = [this.fetchClosedLeadsPipelineData(filters)];

      const dealsPromises = stages
        .filter((stage) => stage !== PipelineStage.CLOSED)
        .map((stage) => this.fetchDealsPipelineData(filters, stage));
      const closedDealsPromises = [this.fetchClosedDealsPipelineData(filters)];

      await Promise.all([
        ...closedLeadsPromises,
        ...leadsPromises,
        ...closedDealsPromises,
        ...dealsPromises,
      ]);
    } catch (error) {
      throw error;
    }
  };

  updatePipelinePage = (
    type: "leads" | "deals",
    stage: number,
    page: number,
  ) => {
    this.pipelinePages[`${type}_page_no_${stage}`] = page;
  };

  fetchIncentivesData = async (filters: SalesAnalyticsSelectedFilters) => {
    try {
      const response = await this.analyticsAPI.getIncentivesData(filters);
      this.incentivesData = response;
    } catch (error) {
      throw error;
    }
  };

  downloadTargetsTemplate = async () => {
    try {
      const response = await this.analyticsAPI.downloadTargetsTemplate();
      return response;
    } catch (error) {
      throw error;
    }
  };

  uploadTargets = async (file: File) => {
    try {
      const response = await this.analyticsAPI.uploadTargets(file);
      return response;
    } catch (error) {
      throw error;
    }
  };
}
